import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ProjectAssignment {
  id: string;
  portal_user_id: string;
  project_id: string;
  assigned_by: string | null;
  assigned_at: string;
  notes: string | null;
  created_at: string;
}

export function useProjectAssignments(portalUserId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['project-assignments', portalUserId],
    queryFn: async () => {
      let query = supabase
        .from('project_assignments')
        .select('*')
        .order('assigned_at', { ascending: false });

      if (portalUserId) {
        query = query.eq('portal_user_id', portalUserId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return (data || []) as ProjectAssignment[];
    },
    enabled: !!portalUserId,
  });

  const createAssignment = useMutation({
    mutationFn: async ({ 
      portalUserId, 
      projectId, 
      notes 
    }: { 
      portalUserId: string; 
      projectId: string; 
      notes?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('project_assignments')
        .insert({
          portal_user_id: portalUserId,
          project_id: projectId,
          assigned_by: user?.id,
          notes,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['inspector-projects'] });
      toast({
        title: "Project toegewezen",
        description: "Het project is succesvol toegewezen aan de gebruiker.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij toewijzen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteAssignment = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('project_assignments')
        .delete()
        .eq('id', assignmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['inspector-projects'] });
      toast({
        title: "Toewijzing verwijderd",
        description: "De projecttoewijzing is verwijderd.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij verwijderen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const bulkAssign = useMutation({
    mutationFn: async ({
      portalUserId,
      projectIds,
    }: {
      portalUserId: string;
      projectIds: string[];
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const assignments = projectIds.map(projectId => ({
        portal_user_id: portalUserId,
        project_id: projectId,
        assigned_by: user?.id,
      }));

      const { error } = await supabase
        .from('project_assignments')
        .insert(assignments);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['inspector-projects'] });
      toast({
        title: "Projecten toegewezen",
        description: "De projecten zijn succesvol toegewezen.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fout bij toewijzen",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    assignments,
    isLoading,
    createAssignment: createAssignment.mutate,
    deleteAssignment: deleteAssignment.mutate,
    bulkAssign: bulkAssign.mutate,
    isCreating: createAssignment.isPending,
    isDeleting: deleteAssignment.isPending,
    isBulkAssigning: bulkAssign.isPending,
  };
}
